import React, { useEffect } from "react";

import { AddProductPropsI } from "./App.types";
import { Drawer } from "./components/layout/Drawer";
import { DrawerContent } from "./components/layout/DrawerContent";
import { Overlay } from "./components/layout/Overlay";
import { GeneralModal } from "./components/modals/GeneralModal";
import { MODAL_TYPES } from "./components/modals/GeneralModal.types";
import {
	getConfigServices,
	getProductData,
	getSubscriptionData,
} from "./slices/actions";
import {
	setBehaviour,
	setIsGamePassAvailable,
	toggleIsVisible,
	toggleModal,
} from "./slices/popupSlice";
import { trackPopupViewEvent } from "./tracking/googleAnalytics";
import { getCookie } from "./utils/helpers/cookie";
import {
	useDispatch,
	useSelector,
	useEventListener,
	useCachedStrings,
} from "./utils/hooks";

import "@komplett/design-system/styles.css";
import "./styles/styles.scss";

export const App = () => {
	const dispatch = useDispatch();
	const maxFullScreenWidth = 692;
	const waitForNextPaint = () =>
		new Promise<void>((resolve) => {
			window.requestAnimationFrame(() => resolve());
		});

	const togglePopup = (shouldBeHidden: boolean) => {
		dispatch(toggleIsVisible(!shouldBeHidden));
		document.body.style.overflow = shouldBeHidden ? "" : "hidden";
	};

	const preparePopupOpen = (
		isAddToCartDrawer?: boolean,
		isGamePassAvailable?: boolean,
	) => {
		if (window.innerWidth < maxFullScreenWidth) {
			history.replaceState({ popupOpen: true }, "", location.pathname);
			history.pushState(null, "", location.pathname);
		}

		dispatch(setBehaviour(isAddToCartDrawer));
		dispatch(setIsGamePassAvailable(isGamePassAvailable));
		togglePopup(false);
	};

	const onPopState = (event) => {
		if (event.state?.popupOpen) togglePopup(true);
	};

	const onAddToCartPopupShow = async ({
		productId,
		renewalPeriod,
		isGamePassAvailable,
		isAddToCartDrawer,
		categoryId,
		configBundleConfigurationId,
	}: AddProductPropsI) => {
		if (getCookie("addToCartPopupConsent") === "False") return;

		preparePopupOpen(isAddToCartDrawer, isGamePassAvailable);
		await waitForNextPaint();

		dispatch(
			getProductData({
				productId,
				renewalPeriod,
				isAddToCartDrawer,
				categoryId,
				configBundleConfigurationId,
			}),
		);
		dispatch(getConfigServices(productId));

		trackPopupViewEvent({ category: "Product", name: "Add to cart" });
	};

	const onSubscriptionDataPopupShow = async ({
		productId,
		price,
		taxRate,
		isConfigBundle,
		subGroup,
		renewalPeriod,
		isGamePassAvailable,
		isAddToCartDrawer,
		isConfigServiceIncluded,
		configServices,
		configBundleConfigurationId,
	}: AddProductPropsI) => {
		if (getCookie("addToCartPopupConsent") === "False") return;

		preparePopupOpen(isAddToCartDrawer, isGamePassAvailable);
		await waitForNextPaint();

		dispatch(
			getSubscriptionData({
				productId,
				price,
				taxRate,
				isConfigBundle,
				subGroup,
				renewalPeriod,
				isConfigServiceIncluded,
				configServices,
				configBundleConfigurationId,
			}),
		);

		trackPopupViewEvent({
			category: "Information",
			name: "Flex subscription",
		});
	};

	const isVisible = useSelector((state) => state.popup.products.isVisible);
	const isModalOpen = useSelector((state) => state.popup.modal.isModalOpen);
	const modalId = useSelector((state) => state.popup.modal.id);
	const insurance = useSelector(
		(state) => state.popup.products.mainProduct.insurance,
	);

	useCachedStrings();

	useEffect(() => {
		if (isVisible || isModalOpen) {
			(document.querySelector("html") as HTMLElement).classList.add(
				"scroll-lock-xs",
			);

			window.addEventListener("popstate", onPopState);
		}

		return () => {
			(document.querySelector("html") as HTMLElement).classList.remove(
				"scroll-lock-xs",
			);
			window.removeEventListener("popstate", onPopState);
		};
	}, [isVisible, isModalOpen]);

	useEventListener({
		type: "openAddToCartPopup",
		handler: onAddToCartPopupShow,
		withData: true,
	});

	useEventListener({
		type: "openSubscriptionDataPopup",
		handler: onSubscriptionDataPopupShow,
		withData: true,
	});

	return (
		<>
			<Overlay
				onOverlayHide={() => {
					togglePopup(true);
					if (
						modalId !== MODAL_TYPES.INSURANCE &&
						!isModalOpen &&
						insurance?.length
					) {
						dispatch(
							toggleModal({
								id: MODAL_TYPES.INSURANCE,
								isOpen: true,
							}),
						);
					}
				}}
			/>
			<Drawer>
				{isVisible && <DrawerContent closePopup={() => togglePopup(true)} />}
			</Drawer>
			<GeneralModal />
		</>
	);
};
